import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { SummaryStage } from '@/features/one-on-one/summary/SummaryStage';
import { PlatformRestriction } from '@/shared/navigation/components/PlatformRestriction';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import {
  useGetEmployeeOneOnOnes,
  useGetManagerOneOnOnes,
  useGetOneOnOneById,
  useUpdateOneOnOne,
} from '@/shared/hooks/useOneOnOne';
import { useMemo, useEffect } from 'react';
import { OneOnOneStatus } from '@momentum/types';
import { useQueryClient } from '@tanstack/react-query';

export const Route = createFileRoute('/_dashboard/one-on-one/$id/summary')({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const employee = useEmployeeContext();
  const navigate = useNavigate();
  const updateOneOnOneMutation = useUpdateOneOnOne();
  const queryClient = useQueryClient();

  // Fetch the one-on-one to check its status
  const { data: oneOnOne } = useGetOneOnOneById(id);

  const { data: employeeOneOnOnes } = useGetEmployeeOneOnOnes({
    employeeId: employee._id,
  });

  const { data: managerOneOnOnes } = useGetManagerOneOnOnes({
    managerId: employee._id,
  });

  const targetEmployeeId = useMemo(() => {
    // Check if this is an employee's one-on-one or manager's
    const employeeOneOnOne = employeeOneOnOnes?.meetings?.find(
      (item) => item._id === id
    );
    const managerOneOnOne = managerOneOnOnes?.find((item) => item._id === id);

    const currentOneOnOne = employeeOneOnOne || managerOneOnOne;

    // Determine which employee's goals to fetch
    // If viewing as manager, fetch the employee's goals (not the manager's)
    return currentOneOnOne?.employeeId || employee._id;
  }, [employeeOneOnOnes, managerOneOnOnes, id, employee._id]);

  // Redirect if status doesn't allow access to summary
  useEffect(() => {
    if (
      oneOnOne &&
      oneOnOne.status !== OneOnOneStatus.READY_FOR_SUBMISSION &&
      oneOnOne.status !== OneOnOneStatus.COMPLETED
    ) {
      navigate({ to: `/one-on-one/${id}/goals-review` });
    }
  }, [oneOnOne, navigate, id]);

  const handleNavigate = (stage: 'goals-review' | 'summary') => {
    if (stage === 'goals-review') {
      navigate({ to: `/one-on-one/${id}/goals-review` });
    }
  };

  const handleSubmit = async (meetingNotes: string[]) => {
    if (!oneOnOne) {
      throw new Error('One-on-one not found');
    }

    if (oneOnOne.managerId.toString() !== employee._id.toString()) {
      throw new Error('Only the manager can submit and lock the one-on-one');
    }

    if (oneOnOne.status !== OneOnOneStatus.READY_FOR_SUBMISSION) {
      throw new Error('One-on-one is not ready for submission');
    }

    // Update the one-on-one to COMPLETED status with notes
    await updateOneOnOneMutation.mutateAsync({
      id,
      data: {
        status: OneOnOneStatus.COMPLETED,
        notes: meetingNotes
          ? {
              manager: meetingNotes,
            }
          : undefined,
      },
    });

    // Invalidate queries
    queryClient.invalidateQueries({ queryKey: ['one-on-one'] });
    queryClient.invalidateQueries({ queryKey: ['goals'] });

    console.log('[One-on-One] Session submitted successfully');

    // Navigate to the completed view
    navigate({ to: `/one-on-one/${id}/completed` });
  };

  if (
    !oneOnOne ||
    (oneOnOne.status !== OneOnOneStatus.READY_FOR_SUBMISSION &&
      oneOnOne.status !== OneOnOneStatus.COMPLETED)
  ) {
    return null;
  }

  return (
    <PlatformRestriction notAllowedPlatforms={['mobile']}>
      <div className="flex min-h-screen flex-col">
        {/* Full-width gradient banner */}
        <div className="h-28 bg-gradient-to-r from-brand-gradient-start via-brand-gradient-mid to-brand-gradient-end md:h-32 rtl:bg-gradient-to-l" />

        {/* Content pulled up to overlap gradient */}
        <div className="-mt-[60px] flex flex-1 flex-col px-4 pb-10 sm:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-[86rem] space-y-6">
            <SummaryStage
              sessionId={id}
              employeeId={targetEmployeeId}
              onNavigate={handleNavigate}
              onSubmit={handleSubmit}
            />
          </div>
        </div>
      </div>
    </PlatformRestriction>
  );
}
